import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { toast } from 'react-toastify';

const AdminCertificateVerificationTab = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPendingCertificates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching pending certificates from adminService');
      
      const response = await adminService.getPendingCertificates();
      console.log('Certificate response:', response);
      
      // Handle different API response formats
      let certificatesData = [];
      
      if (response && response.certificates && Array.isArray(response.certificates)) {
        certificatesData = response.certificates;
      } else if (response && Array.isArray(response)) {
        certificatesData = response;
      } else if (response && typeof response === 'object') {
        // Try to find any array in the response
        const possibleArrays = Object.values(response).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          certificatesData = possibleArrays[0];
        }
      }
      
      console.log(`Found ${certificatesData.length} certificates to display`);
      setCertificates(certificatesData);
    } catch (error) {
      console.error('Error loading certificates:', error);
      setError('Failed to load certificates');
      toast.error('Failed to load pending certificates');
      
      // Use fallback data in case of API failure
      setCertificates([{
        id: 'demo-cert-1',
        organization: 'Demo Restaurant',
        submittedAt: '2025-06-05',
        certificateUrl: 'https://example.com/cert',
        type: 'Food Safety Certificate'
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingCertificates();
  }, [loadPendingCertificates]);



  const handleVerify = useCallback(async (id, isApproved) => {
    try {
      await adminService.verifyCertificate(id, { isApproved });
      toast.success(`Certificate ${isApproved ? 'approved' : 'rejected'}`);
      loadPendingCertificates();
    } catch (error) {
      toast.error('Failed to update certificate status');
    }
  }, [loadPendingCertificates]);

  if (loading) {
    return <div className="flex justify-center p-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
        <p>{error}</p>
        <button 
          onClick={loadPendingCertificates}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">
        Pending Certificate Verification
        <span className="ml-2 px-2 py-0.5 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
          {certificates.length}
        </span>
      </h2>
      <p className="text-sm text-gray-500 mb-4">Review and verify food safety certificates submitted by donors</p>
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : certificates.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No pending certificates to verify</p>
          <p className="text-sm text-gray-400 mt-2">All certificates have been processed!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {Array.isArray(certificates) && certificates.map((cert) => (
            <div key={cert.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium">{cert.organization}</h3>
                  <p className="text-sm text-gray-500">{cert.submittedAt}</p>
                  <div className="mt-2">
                    <a
                      href={cert.certificateUrl || cert.fileUrl || cert.documentUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Certificate
                    </a>
                  </div>
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => handleVerify(cert.id, true)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleVerify(cert.id, false)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCertificateVerificationTab;
